
import { Component, Vue } from "vue-property-decorator";
import { Form, Message } from "element-ui";
import { cloneDeep } from "lodash";
import { IDeliveryNoteModel, IStorageModel, IUserModel } from "@/api/types";
import {
  getAvailableStorageLayers,
  getStorages
} from "@/api/storages";
import {
  addRemark,
  cancelReserved,
  createDeliveryNote,
  defaultDeliveryNoteData,
  deleteDeliveryNote,
  exportDeliveryNotes,
  getDeliveryNotes,
  getDeliveryNoteStateList,
  getStorageLayers,
  updateSailingSchedule,
  updateStorageLayers,
  updateMaxVolume,
  postRemove
} from "@/api/delivery-note";
import { createCustomerSimple, getCustomer } from "@/api/customer";
import Pagination from "@/components/Pagination/index.vue";
import { getUsersByJobPosition, getUsersByJobPositions } from "@/api/users";
import { HqPackingListType } from "@/commons/HqPackingListType";
import {
  createEmptyHqPackingList,
  uploadPackingList,
} from "@/api/hq-packing-list";
import { COMPANY_OPTIONS } from "@/constant";
import { ElUploadInternalFileDetail } from "element-ui/types/upload";
import { findByCode } from "@/api/supplier";
import { statisticsCustomsPackingData } from "@/api/customs-packing";

@Component({
  name: "DeliveryNoteManagement",
  components: {
    Pagination,
  },
})
export default class extends Vue {
  private tableKey = 0;
  private deliveryNoteList: IDeliveryNoteModel[] = [];
  private sellerList: IUserModel[] = [];
  private purchaserList: IUserModel[] = [];
  private logisticsClerkList: IUserModel[] = [];
  private total = 0;
  private listLoading = true;
  private showFileList = false;
  private batchUploadDialogVisible = false;
  private buttonLoading = false;
  private files: any[] = [];
  private listQuery = {
    pageNumber: 1,
    pageSize: 20,
    code: null,
    hqPackingListCode: null,
    startDate: null,
    endDate: null,
    customerAbbreviationOrCode: null,
    inStates: null,
    outStates: null,
    storageLayerIds: null,
    sellerName: null,
    logisticsClerkName: null,
    selectedStorageLayer: [],
    purchaserName: null,
  };
  private allStorageLayerList: IStorageModel[] = [];
  private availableStorageLayerList: IStorageModel[] = [];
  private popoverVisible = false;

  private companyCodeOptions = COMPANY_OPTIONS;
  private customerAbbreviation = "";
  private JobPositionType = {
    seller: "seller",
    purchaser: "purchaser",
    logisticsClerk: "logistics-clerk",
  };
  private dialogFormVisible = false;
  private dialogSelectStorageLayerVisible = false;
  private dialogStatus = "";
  private actionName = {
    update: "编辑",
    create: "新建",
    addRemark: "添加备注",
    updateSailingSchedule: "修改船期",
    updateStorageLayers: "更改仓位",
  };

  private getHiddenColumns() {
    if (
      this.$route.meta.buttonCodes &&
      Array.isArray(this.$route.meta.buttonCodes)
    ) {
      return this.$route.meta.buttonCodes.includes("actionColumns");
    }
  }

  statistics = new Statistics();

  private payload = new WriteDeliveryNoteInput();

  private rules = {
    companyCode: [{ required: true, message: "请选择公司", trigger: "blur" }],
    customerCode: [
      { required: true, message: "请输入客户编码", trigger: "blur" },
    ],
    customerAbbreviation: [
      { required: true, message: "请输入客户简称", trigger: "blur" },
    ],
    factoryCode: [
      { required: true, message: "请输入工厂编号", trigger: "blur" },
    ],
    factoryName: [
      { required: true, message: "请输入工厂名称", trigger: "blur" },
    ],
    purchaser: [{ required: true, message: "请选择采购员", trigger: "blur" }],
    estimatedVolume: [
      { required: true, message: "请输入预计体积", trigger: "blur" },
    ],
    deliveryNoteCode: [
      { required: true, message: "请输入进仓单编号", trigger: "blur" },
    ],
  };

  private cascaderProps = {
    multiple: true,
    emitPath: false,
    value: "id",
    label: "name",
  };
  private downloadLoading = false;
  private tempData = defaultDeliveryNoteData;
  private deliveryNoteInStateList: [{ name: string; desc: string }] = [
    { name: null, desc: null },
  ];
  private deliveryNoteOutStateList: [{ name: string; desc: string }] = [
    { name: null, desc: null },
  ];
  private jobPositionAndUsersMap = {};
  JOB_POSITION_NAMES: string[] = ["PURCHASER"];

  created() {
    this.initSellerList();
    this.initLogisticsClerkList();
    this.initPurchaserList();
    this.initAllStorageLayerList();
    this.initDeliveryNoteStateList();
    this.getList();
  }

  private async searchFactory() {
    const factoryCode = this.payload.factoryCode;
    if (!factoryCode) {
      Message.error({
        message: "请输入工厂编号",
        duration: 2000,
      });
      return;
    }
    const { name, purchaser }: any = await findByCode(factoryCode);
    this.payload.factoryName = name;
    this.payload.purchaser = purchaser;
    this.$forceUpdate();
  }

  private async createHqPackingList() {
    const data = {
      deliveryNoteCode: this.payload.deliveryNoteCode,
      factoryName: this.payload.factoryName,
      purchaser: this.payload.purchaser,
      estimatedVolume: this.payload.estimatedVolume,
    };
    const loading = this.$loading({
      lock: true,
      text: 'Loading',
      spinner: 'el-icon-loading',
      background: 'rgba(0, 0, 0, 0.7)'
    });
    await createEmptyHqPackingList(data).then(async res => {
      this.createHqPackingListVisible = false;
      this.payload.estimatedVolume = undefined;
      await this.getList();
      loading.close();
    }).catch(err => {
      console.log(err);
      loading.close();
    })
  }

  private gotoDocumentFlow(data) {
    this.$router.push({
      name: "DocumentFlow",
      params: { type: "DELIVERY_NOTE", documentId: data.id },
    });
  }

  private async initDeliveryNoteStateList() {
    const { out, inStates }: any = await getDeliveryNoteStateList();
    this.deliveryNoteInStateList = inStates;
    this.deliveryNoteOutStateList = out;
  }

  private async getList() {
    if (this.listQuery.startDate > this.listQuery.endDate) {
      Message({
        message: "结束日期必须大于起始日期",
        type: "error",
        duration: 2 * 1000,
      });
      return;
    }
    this.listLoading = true;
    const params = Object.assign({}, this.listQuery);
    if (params.selectedStorageLayer) {
      params.storageLayerIds = params.selectedStorageLayer.join(",");
      params.selectedStorageLayer = null;
    }
    const { total, content }: any = await getDeliveryNotes(params).finally(
      () => {
        this.listLoading = false;
      }
    );
    this.total = total;
    this.deliveryNoteList = content;
  }

  private async getAvailableStorageLayers() {
    const data: any = await getAvailableStorageLayers({
      isReserved: this.payload.reserved,
      deliveryNoteId: this.payload.id,
    });
    this.availableStorageLayerList = data;
  }

  private handleFilter() {
    this.listQuery.pageNumber = 1;
    this.getList();
  }

  private async handleExport() {
    if (this.listQuery.startDate > this.listQuery.endDate) {
      Message({
        message: "结束日期必须大于起始日期",
        type: "error",
        duration: 2 * 1000,
      });
      return;
    }
    const params = Object.assign({}, this.listQuery);
    if (params.selectedStorageLayer) {
      params.storageLayerIds = params.selectedStorageLayer.join(",");
      params.selectedStorageLayer = null;
    }
    await exportDeliveryNotes(params);
  }

  private resetTempData() {
    this.tempData = cloneDeep(defaultDeliveryNoteData);
    this.payload = new WriteDeliveryNoteInput();
  }

  private createHqPackingListVisible = false;

  private async handleCreateHqPackingList() {
    await this.initJobPositionUsers();
    this.createHqPackingListVisible = true;
  }

  private async initJobPositionUsers() {
    if (Object.keys(this.jobPositionAndUsersMap).length < 1) {
      const data: any = await getUsersByJobPositions({
        jobPositionTypes: this.JOB_POSITION_NAMES.join(","),
      });
      this.jobPositionAndUsersMap = data;
    }
  }

  private uploadRouter() {
    this.$router.push({
      name: "UploadFactoryPackingList"
    });
  }

  private handleCreate() {
    this.resetTempData();
    this.dialogStatus = "create";
    this.dialogFormVisible = true;
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate();
    });
  }

  private doCreateData() {
    if (!this.customerAbbreviation) {
      Message.error("请输入客户简称");
      return;
    }
    (this.$refs.dataForm as Form).validate(async valid => {
      if (valid) {
        if (!this.payload.customerId) {
          const { id }: any = await createCustomerSimple({
            name: "",
            // abbreviation: this.payload.customerAbbreviation,
            abbreviation: this.customerAbbreviation,
            code: this.payload.customerCode,
          });
          this.payload.customerId = id;
        }
        await createDeliveryNote(this.payload);
        this.dialogFormVisible = false;
        this.$notify({
          title: "成功",
          message: "创建成功",
          type: "success",
          duration: 2000,
        });
        await this.getList();
      }
    });
  }

  private selectRows = [] as any;
  private vacatePositionLoading = false;
  private async vacatePosition() {
    if(this.selectRows.length === 0) {
      this.$notify({
        title: "提示",
        message: "请选择数据！",
        type: "warning",
        duration: 2000,
      });
      return
    }
    this.vacatePositionLoading = true;
    this.$confirm("确定腾空仓位吗？", "提示", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    })
    .then(async () => {
      let data = this.selectRows.map(e => {return e.id})
      await postRemove(data).then(res => {
        this.$notify({
          title: "提示",
          message: "操作成功",
          type: "success",
          duration: 2000,
        });
        (this.$refs.multipleTable as any).clearSelection();
        this.vacatePositionLoading = false;
      }).catch(err => {
        console.log(err);
        this.vacatePositionLoading = false;
      })
    })
    .catch((err) => {
      console.error(err);
      this.vacatePositionLoading = false;
    });
  }

  private handleUpdate(row: any) {
    this.tempData = Object.assign({}, row);
    // this.tempData.timestamp = +new Date(this.tempData.timestamp)
    this.dialogStatus = "update";
    this.dialogFormVisible = true;
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate();
    });
  }

  private async handleDelete(row: any) {
    await deleteDeliveryNote(row.id);
    this.$notify({
      title: "Success",
      message: "删除成功",
      type: "success",
      duration: 2000,
    });
    await this.getList();
  }

  private onDialogConfirmed() {
    if (this.dialogStatus === "create") {
      this.doCreateData();
    } else if (this.dialogStatus === "addRemark") {
      this.doAddRemark();
    } else if (this.dialogStatus === "updateSailingSchedule") {
      this.doUpdateSailingSchedule();
    } else if (this.dialogStatus === "updateStorageLayers") {
      this.doUpdateStorageLayers();
    } else if (this.dialogStatus === "updateMaxVolume") {
      this.doUpdateMaxVolume();
    }
  }

  private showPackingList(row: IDeliveryNoteModel) {
    this.$router.push({
      name: "DeliveryPackingList",
      params: { type: "delivery-note", parentId: row.id, canUpload: String(row.canUpload) },
    });
  }

  private handleSelectionChange(rows) {
    this.selectRows = rows
  }

  /**
   * 因布局问题，这个晢时不用。
   */
  copyToPasteBoard(text) {
    console.log(text)
    // this.$copyText(text).then(() => {
    //   this.$notify({
    //     title: "成功",
    //     message: "成功复制 " + text,
    //     type: "success",
    //     duration: 2000
    //   });
    // },() => {
    //   this.$notify({
    //     title: "失败",
    //     message: "复制失败!",
    //     type: "warning",
    //     duration: 2000
    //   });
    // })
  }

  // private h5Copy(content) {
  //   if (!document.queryCommandSupported("copy")) {
  //     // 不支持
  //     return false;
  //   }
  //   let textarea = document.createElement("textarea") as any;
  //   textarea.value = content;
  //   textarea.readOnly = "readOnly";
  //   document.body.appendChild(textarea);
  //   textarea.select(); // 选择对象
  //   textarea.setSelectionRange(0, content.length); // 核心
  //   let result = document.execCommand("copy"); // 执行浏览器复制命令
  //   textarea.remove();
  //   this.$notify({
  //     title: "成功",
  //     message: "复制成功",
  //     type: "success",
  //     duration: 2000
  //   });
  //   return result;
  // }

  /**
   * 获取进仓单的体积详情
   * @param row
   */
  async fetchVolumeDetails(id) {
    const data = (await statisticsCustomsPackingData(id)) as Statistics;
    this.statistics = { ...data };
  }

  private async cancelReserved(id: string) {
    await cancelReserved(id);
    this.$notify({
      title: "Success",
      message: "操作成功",
      type: "success",
      duration: 2000,
    });
    await this.getList();
  }

  private async searchCustomer() {
    const customerCode = this.payload.customerCode;
    this.payload.customerAbbreviation = "";
    this.customerAbbreviation = "";
    if (customerCode) {
      const { id, abbreviation, seller, logistics }: any = await getCustomer(
        customerCode
      );
      // this.payload.customerId = id
      this.payload.customerId = id;
      this.payload.customerAbbreviation = abbreviation;
      this.payload.sellerId = seller;
      this.payload.logisticsClerkId = logistics;
      this.customerAbbreviation = abbreviation;
      this.$forceUpdate();
    }
  }

  private showDialog(action: string, row: IDeliveryNoteModel) {
    this.payload = new WriteDeliveryNoteInput(
      row?.id,
      row?.remark,
      row?.estimatedSailingSchedule,
      row?.maxVolume
    );
    if (action === "updateStorageLayers") {
      this.getAvailableStorageLayers();
      this.getDeliveryNoteStorageLayers();
    }
    this.dialogStatus = action;
    this.dialogFormVisible = true;
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate();
    });
  }

  private clearFiles() {
    this.files = [];
  }

  private handleRemove(
    file: ElUploadInternalFileDetail,
    fileList: ElUploadInternalFileDetail[]
  ) {
    let fileDetails: any = fileList.filter((value) => value.name !== file.name);
    this.files = fileDetails;
  }

  private async getDeliveryNoteStorageLayers() {
    const data: any = await getStorageLayers(this.payload.id);
    this.payload.storageLayerIds = data.map((value) => value.id);
  }

  private async doAddRemark() {
    if (this.payload.remark) {
      await addRemark(this.payload.id, { remark: this.payload.remark });
      this.dialogFormVisible = false;
      this.$notify({
        title: "Success",
        message: "操作成功",
        type: "success",
        duration: 2000,
      });
      await this.getList();
    }
  }

  private async doUpdateSailingSchedule() {
    if (this.payload.estimatedSailingSchedule) {
      await updateSailingSchedule(this.payload.id, {
        estimatedSailingSchedule: this.payload.estimatedSailingSchedule,
      });
      this.dialogFormVisible = false;
      this.$notify({
        title: "Success",
        message: "操作成功",
        type: "success",
        duration: 2000,
      });
      await this.getList();
    }
  }

  private async doUpdateStorageLayers() {
    if (this.payload.storageLayerIds) {
      await updateStorageLayers(this.payload.id, {
        storageLayerIds: this.payload.storageLayerIds,
      });
      this.dialogFormVisible = false;
      this.$notify({
        title: "Success",
        message: "操作成功",
        type: "success",
        duration: 2000,
      });
      await this.getList();
    }
  }

  private async initSellerList() {
    const data: any = await getUsersByJobPosition(this.JobPositionType.seller);
    this.sellerList = data;
  }

  private async initLogisticsClerkList() {
    const data: any = await getUsersByJobPosition(
      this.JobPositionType.logisticsClerk
    );
    this.logisticsClerkList = data;
  }

  private async initPurchaserList() {
    const data: any = await getUsersByJobPosition(
      this.JobPositionType.purchaser
    );
    this.purchaserList = data;
  }

  private async initAllStorageLayerList() {
    const data: any = await getStorages();
    this.allStorageLayerList = data;
  }

  private addFile(file: File) {
    if (file.size / 1024 / 1024 >= 100) {
      Message({
        message: "上传的文件大小不能超过100M!",
        type: "error",
        duration: 2 * 1000,
      });
      return;
    }
    this.files.push(file);
  }

  private getCanUploadText(row) {
    if (row.canUpload !== null && !row.canUpload === true) {
      return "禁止上传";
    } else {
      return "可上传";
    }
  }

  private async handleUpload() {
    let formData = new window.FormData();
    formData.append("files", this.files.pop());
    formData.append("type", HqPackingListType.DELIVERY_NOTE.toUpperCase());
    await uploadPackingList(formData);
    this.$notify({
      title: "成功",
      message: "操作成功",
      type: "success",
      duration: 2000,
    });
    await this.getList();
  }

  private async handleBatchUpload() {
    let formData = new window.FormData();
    this.files.forEach((file) => formData.append("files", file.raw));
    formData.append("type", HqPackingListType.DELIVERY_NOTE.toUpperCase());
    await uploadPackingList(formData)
      .then(() => {
        this.batchUploadDialogVisible = false;
      })
      .catch(() => {
        this.batchUploadDialogVisible = false;
      });
    this.$notify({
      title: "成功",
      message: "操作成功",
      type: "success",
      duration: 2000,
    });
    this.clearFiles();
    await this.getList();
  }

  private async doUpdateMaxVolume() {
    if (this.payload.maxVolume) {
      await updateMaxVolume(this.payload.id, {
        maxVolume: this.payload.maxVolume,
      });
      this.dialogFormVisible = false;
      this.$notify({
        title: "Success",
        message: "操作成功",
        type: "success",
        duration: 2000,
      });
      await this.getList();
    }
  }

  private renderCusAbbr(prop, row) {
    if (row.pcName && row.pcName.length > 0){
      return `[${row.pcName}]${prop}`
    }
    return prop
  }
}

class WriteDeliveryNoteInput {
  id: string;
  companyCode: string;
  customerId?: string;
  customerAbbreviation: string;
  customerCode: string;
  remark: string;
  estimatedSailingSchedule: string;
  storageLayerIds: [];
  logisticsClerkId: string;
  sellerId: string;
  purchaserId: string;
  reserved: boolean;
  maxVolume: string;
  deliveryNoteCode?: string;
  factoryCode?: string;
  factoryName?: string;
  purchaser?: string;
  estimatedVolume?: number;

  constructor(
    id?: string,
    remark?: string,
    estimatedSailingSchedule?: string,
    maxVolume?: string
  ) {
    this.id = id;
    this.remark = remark;
    this.estimatedSailingSchedule = estimatedSailingSchedule;
    this.maxVolume = maxVolume;
  }
}

class Statistics {
  productVolumeInStock: number; // 累积入库体积
  productVolumeNotInStock: number; // 未入库体积
  productGrossWeightInStockInFact: number; // 实际在库毛重
  productVolumeInStockInFact: number; // 实际在库体积
  estimatedVolumeNotInStock: number; // 未入库预估体积
}
